import produce from "immer";
import { dateToDateTime } from "lib";
import {
    BooleanSearch,
    DateSearch,
    IntegerSearch,
    NumberSearch,
    PercentSearch,
    SearchDef,
    SearchDefinition,
    StringSearch,
    TypeSearch,
} from "lib/src/search/types";
import React, { useMemo } from "react";
import { useTranslation } from "locales";
import { View } from "react-native";
import { Divider } from "react-native-paper";
import { StyleFunction, useTheme, useThemedStyle } from "../../theme";
import { Button } from "../button";
import { CheckBox } from "../check-box";
import { DateTimePickerInput } from "../datetime-picker-input";
import { Form } from "../form";
import { IconButton } from "../icon-button";
import { InputControl } from "../input-control";
import { InputLabel } from "../input-label";
import { NumberInput } from "../number-input";
import { Picker } from "../picker";
import { Spacer } from "../spacer";
import { TextInput } from "../textinput";
import { RadioButton } from "react-native-paper";

export function SearchForm(props: {
    form: Form<SearchDefinition>;
    onSubmit: () => void;
    onCloseSearch: () => void;
    visible: boolean;
    hideCloseButton?: boolean;
}) {
    const [t] = useTranslation();
    const theme = useTheme();
    const styles = useThemedStyle(styleFunc);

    const [{ values }, { setValue, handleSubmit }] = props.form;

    const components = useMemo(() => {
        if (!values) {
            return null;
        }

        return Object.keys(values).map((elem: keyof SearchDefinition) => {
            const element = values[elem];
            let component;
            switch (element.type) {
                case "amount":
                    component = (
                        <NumberInput
                            label={element.name}
                            placeholder={t(
                                "searching.enter",
                                "Enter {{name}}",
                                { name: element.name }
                            )}
                            onChange={text => {
                                setValue(
                                    elem as keyof SearchDef,
                                    produce(element, draft => {
                                        draft.value = text ?? 0;

                                        // If the search field is not yet enabled, then do this, since the user is fiddling with it.
                                        draft.enabled =
                                            text === null ? false : true;
                                    })
                                );
                            }}
                            defaultValue={element.value}
                        />
                    );
                    break;
                case "percent":
                    component = (
                        <NumberInput
                            label={element.name}
                            placeholder={t(
                                "searching.enter",
                                "Enter {{name}}",
                                { name: element.name }
                            )}
                            unit="%"
                            min={0} // rate, equal to 0%
                            max={1} // rate, equal to 100%
                            percentageToRate={true}
                            decimals={2}
                            onChange={text => {
                                setValue(
                                    elem,
                                    produce(element as PercentSearch, draft => {
                                        draft.value = text ?? 0;

                                        // If the search field is not yet enabled, then do this, since the user is fiddling with it.
                                        draft.enabled =
                                            text === null ? false : true;
                                    })
                                );
                            }}
                            defaultValue={element.value}
                        />
                    );
                    break;
                case "boolean":
                    component = (
                        <>
                            <InputLabel>{element.name}</InputLabel>
                            <Spacer space={0.5} />
                            <RadioButton.Group
                                value={String(element.value)}
                                onValueChange={value => {
                                    setValue(
                                        elem,
                                        produce(
                                            element as BooleanSearch,
                                            draft => {
                                                draft.value = value === "true";

                                                // If the search field is not yet enabled, then do this, since the user is fiddling with it.
                                                if (!element.enabled) {
                                                    draft.enabled = true;
                                                }
                                            }
                                        )
                                    );
                                }}
                            >
                                {element.values.map(item => (
                                    <View
                                        style={theme.styles.row}
                                        key={`option_${item.value}`}
                                    >
                                        <RadioButton.Item
                                            label={item.label}
                                            value={String(item.value)}
                                            position="leading"
                                            style={styles.radioButton}
                                            rippleColor="transparent"
                                            color={
                                                element.value
                                                    ? theme.colors.secondary
                                                    : theme.colors.textDark
                                            }
                                        />
                                    </View>
                                ))}
                            </RadioButton.Group>
                        </>
                    );
                    break;
                case "string":
                    component = (
                        <TextInput
                            label={element.name}
                            placeholder={t(
                                "searching.enter",
                                "Enter {{name}}",
                                { name: element.name.toLowerCase() }
                            )}
                            onChangeText={text => {
                                setValue(
                                    elem,
                                    produce(element as StringSearch, draft => {
                                        draft.value = text;

                                        // If the search field is not yet enabled, then do this, since the user is fiddling with it.
                                        draft.enabled =
                                            text === "" ? false : true;
                                    })
                                );
                            }}
                            defaultValue={element.value}
                            onSubmitEditing={handleSubmit(props.onSubmit)}
                            blurOnSubmit={false}
                        />
                    );
                    break;
                case "number":
                    component = (
                        <NumberInput
                            label={element.name}
                            placeholder={t(
                                "searching.enter",
                                "Enter {{name}}",
                                { name: element.name }
                            )}
                            onChange={text => {
                                setValue(
                                    elem,
                                    produce(element as NumberSearch, draft => {
                                        draft.value = text ?? 0;

                                        // If the search field is not yet enabled, then do this, since the user is fiddling with it.
                                        draft.enabled =
                                            text === null ? false : true;
                                    })
                                );
                            }}
                            defaultValue={element.value}
                            onSubmitEditing={handleSubmit(props.onSubmit)}
                        />
                    );
                    break;
                case "integer":
                    component = (
                        <NumberInput
                            label={element.name}
                            placeholder={t(
                                "searching.enter",
                                "Enter {{name}}",
                                { name: element.name }
                            )}
                            onChange={newNumberValue => {
                                setValue(
                                    elem,
                                    produce(element as IntegerSearch, draft => {
                                        draft.value = newNumberValue ?? 0;

                                        // If the search field is not yet enabled, then do this, since the user is fiddling with it.
                                        draft.enabled =
                                            newNumberValue === null
                                                ? false
                                                : true;
                                    })
                                );
                            }}
                            defaultValue={element.value}
                            decimals={0}
                            onSubmitEditing={handleSubmit(props.onSubmit)}
                        />
                    );
                    break;
                case "date":
                    component = (
                        <>
                            <InputLabel>{element.name}</InputLabel>
                            <DateTimePickerInput
                                dateTimeType="dateTime"
                                onChangeDate={dateObj => {
                                    if (dateObj === undefined) {
                                        return;
                                    }
                                    setValue(
                                        elem,
                                        produce(
                                            element as DateSearch,
                                            draft => {
                                                draft.values.from = dateObj;

                                                // If the search field is not yet enabled, then do this, since the user is fiddling with it.
                                                if (!element.enabled) {
                                                    draft.enabled = true;
                                                }
                                            }
                                        )
                                    );
                                }}
                                value={dateToDateTime(element.values.from)}
                            />
                            <DateTimePickerInput
                                dateTimeType="dateTime"
                                onChangeDate={dateObj => {
                                    if (dateObj === undefined) {
                                        return;
                                    }
                                    setValue(
                                        elem,
                                        produce(
                                            element as DateSearch,
                                            draft => {
                                                draft.values.to = dateObj;

                                                // If the search field is not yet enabled, then do this, since the user is fiddling with it.
                                                if (!element.enabled) {
                                                    draft.enabled = true;
                                                }
                                            }
                                        )
                                    );
                                }}
                                value={dateToDateTime(element.values.to)}
                            />
                        </>
                    );
                    break;
                case "type":
                    component = (
                        <Picker
                            label={element.name}
                            onValueChange={value => {
                                if (value === undefined) {
                                    return;
                                }
                                setValue(
                                    elem,
                                    produce(element as TypeSearch, draft => {
                                        draft.value = value;

                                        // If the search field is not yet enabled, then do this, since the user is fiddling with it.
                                        if (!element.enabled) {
                                            draft.enabled = true;
                                        }
                                    })
                                );
                            }}
                            selectedValue={element.value}
                        >
                            {element.values.map(item => (
                                <Picker.Item
                                    key={`search-${element.name}-${item.value}`}
                                    label={item.name}
                                    value={item.value}
                                />
                            ))}
                        </Picker>
                    );
                    break;
            }

            return (
                <View
                    key={`search-${element.name}`}
                    style={styles.searchComponent}
                >
                    <View style={styles.checkbox}>
                        <CheckBox
                            onValueChange={value =>
                                setValue(
                                    elem,
                                    produce(element, draft => {
                                        draft.enabled = value;
                                    })
                                )
                            }
                            value={element.enabled}
                        />
                    </View>
                    <Spacer />
                    <InputControl
                        style={theme.styles.flex}
                        description={element.description}
                    >
                        {component}
                    </InputControl>
                    <Spacer space={3} />
                </View>
            );
        });
    }, [
        handleSubmit,
        props.onSubmit,
        setValue,
        styles.checkbox,
        styles.radioButton,
        styles.searchComponent,
        t,
        theme.colors.secondary,
        theme.colors.textDark,
        theme.styles.flex,
        theme.styles.row,
        values,
    ]);

    return props.visible ? (
        <View>
            <View style={styles.formContainer}>
                <View style={styles.searchComponents}>{components}</View>
                {!props.hideCloseButton ? (
                    <View style={theme.styles.row}>
                        <Spacer />
                        <IconButton
                            name="close"
                            onPress={props.onCloseSearch}
                            color={theme.colors.black}
                        />
                    </View>
                ) : null}
            </View>
            <View style={theme.styles.row}>
                <Button onPress={handleSubmit(props.onSubmit)} size="small">
                    {t("common.search", "Search")}
                </Button>
            </View>
            <Spacer space={2} />
            <Divider />
        </View>
    ) : null;
}

const styleFunc: StyleFunction = (theme, dimensions) => ({
    formContainer: {
        ...theme.styles.row,
        ...theme.styles.flex1,

        justifyContent: "space-between",
    },

    searchComponents: {
        ...theme.styles.row,
        ...theme.styles.flex1,
        ...theme.styles.flexWrap,
    },
    searchComponent: {
        ...theme.styles.row,
        width:
            dimensions.width > theme.breakpoint.largeTabletWidth
                ? "25%"
                : dimensions.width > theme.breakpoint.tabletWidth
                ? "33.3%"
                : "100%",
        minWidth: 250,
    },

    checkbox: {
        paddingTop: theme.spacingScale * 2.5, // Adjusted to fit the text height in the input fields.
    },
    radioButton: {
        padding: 0,
        paddingRight: theme.spacingScale,
    },
});
